import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { faSlidersH } from '@fortawesome/pro-light-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { SetPreferredSetting } from '@wilson/preferred-settings/state';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ShiftTimelineKPISettingPopupComponent } from '../shift-timeline-kpi-setting-popup/shift-timeline-kpi-setting-popup.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  KPIOptions,
  StaffLayoutStyle,
  COMPACT_ROW_HEIGHT,
  COMPACT_SHIFT_HEIGHT,
  MINIMAL_ROW_HEIGHT,
  MINIMAL_SHIFT_HEIGHT,
  TimelineAssignmentLayoutQueryParamKey,
} from '@wilson/interfaces';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import {
  SetDefaultRowHeight,
  SetDefaultShiftHeight,
} from '@wilson/shift-timeline/state';
import { FilterService } from '@wilson/filter';
import { NzSafeAny } from 'ng-zorro-antd/core/types';

@Component({
  selector: 'wilson-shift-timeline-kpi-setting-button',
  templateUrl: './shift-timeline-kpi-setting-button.component.html',
  styleUrls: ['./shift-timeline-kpi-setting-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    NzSelectModule,
    NzRadioModule,
    FormsModule,
    FontAwesomeModule,
    NzButtonModule,
  ],
})
export class ShiftTimelineKPISettingButtonComponent {
  private readonly store = inject(Store);
  private readonly nzModalService = inject(NzModalService);
  private readonly translateService = inject(TranslateService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly filterService = inject(FilterService);

  faSlidersH = faSlidersH;

  openKPISettingModal(): void {
    const modalRef =
      this.nzModalService.create<ShiftTimelineKPISettingPopupComponent>({
        nzContent: ShiftTimelineKPISettingPopupComponent,
        nzCentered: true,
        nzTitle: this.translateService.instant(
          'shift_timeline.kpi_setting.title',
        ),
        nzFooter: [
          {
            label: this.translateService.instant('general.cancel'),
            type: 'default',
            onClick: (): NzSafeAny => {
              modalRef.destroy();
            },
          },
          {
            label: this.translateService.instant('general.apply'),
            type: 'primary',
            onClick: async (
              modalInstance: ShiftTimelineKPISettingPopupComponent,
            ): Promise<NzSafeAny> => {
              const userId = this.store.selectSnapshot(AuthState.userId);
              if (userId) {
                this.filterService.saveValueInQueryParam(
                  TimelineAssignmentLayoutQueryParamKey,
                  modalInstance.selectedAssignmentLayoutStyle,
                );
                this.store.dispatch([
                  new SetPreferredSetting(userId, {
                    staffLayout: modalInstance.selectedStaffLayoutStyle,
                    assignmentLayout:
                      modalInstance.selectedAssignmentLayoutStyle,
                    mainKPIs: modalInstance.selectedKPIs,
                    sortBy: modalInstance.selectedSortByOption,
                    sortDirection: modalInstance.selectedSortDirection,
                    threshold: modalInstance.selectedThreshold,
                    shiftColorOption: modalInstance.selectedShiftColorOption,
                  }),
                  new SetDefaultRowHeight(
                    modalInstance.selectedStaffLayoutStyle ===
                    StaffLayoutStyle.Compact
                      ? COMPACT_ROW_HEIGHT
                      : MINIMAL_ROW_HEIGHT,
                  ),
                  new SetDefaultShiftHeight(
                    modalInstance.selectedStaffLayoutStyle ===
                    StaffLayoutStyle.Compact
                      ? COMPACT_SHIFT_HEIGHT
                      : MINIMAL_SHIFT_HEIGHT,
                  ),
                ]);

                const kpiOptionsToCheck = [
                  KPIOptions.Productivity,
                  KPIOptions.OvertimeWeek,
                  KPIOptions.OvertimeMonth,
                  KPIOptions.PlannedTimeWeek,
                  KPIOptions.PlannedTimeMonth,
                  KPIOptions.WorkTimeWeek,
                  KPIOptions.WorkTimeMonth,
                ];

                const shouldRefreshProductivity =
                  modalInstance.selectedKPIs.some((kpi) =>
                    kpiOptionsToCheck.includes(kpi),
                  );
                const shouldRefreshVacationBalance =
                  modalInstance.selectedKPIs.includes(
                    KPIOptions.VacationBalance,
                  );

                if (shouldRefreshProductivity) {
                  this.timelineCommonOperationsService.refreshTotalProductivityAllFilteredUsers(
                    true,
                  );
                }
                if (shouldRefreshProductivity || shouldRefreshVacationBalance) {
                  this.timelineCommonOperationsService.refreshKpisForVisibleUsers(
                    true,
                  );
                }
                this.timelineCommonOperationsService.refreshUnassignedRegion();
              }
              modalRef.destroy();
            },
          },
        ],
        nzOkText: this.translateService.instant('general.apply'),
      });
  }
}
